/**
 * Hook: useDevices
 *
 * Gestisce operazioni CRUD per gli apparati/dispositivi.
 * Utilizza il context WhatsAppProvider per l'URL delle API.
 *
 * @example
 * ```tsx
 * function DeviceList() {
 *   const { devices, loading, createDevice, deleteDevice } = useDevices();
 *
 *   if (loading) return <Spinner />;
 *
 *   return (
 *     <ul>
 *       {devices.map(d => <li key={d.id}>{d.device_name}</li>)}
 *     </ul>
 *   );
 * }
 * ```
 */

import { useState, useCallback } from 'react';
import { useWhatsAppConfig } from '../WhatsAppProvider';
import type { Device, APIResponse } from '../types';
import { waAuthHeaders } from '../waFetch';

/**
 * Risultato dell'hook useDevices
 */
export interface UseDevicesResult {
  /** Lista dispositivi caricati */
  devices: Device[];
  /** Stato di caricamento */
  loading: boolean;
  /** Messaggio di errore se presente */
  error: string | null;
  /** Ricarica lista dispositivi */
  refresh: (activeOnly?: boolean) => Promise<void>;
  /** Crea nuovo dispositivo */
  createDevice: (device: Partial<Device>) => Promise<boolean>;
  /** Aggiorna dispositivo esistente */
  updateDevice: (id: number, updates: Partial<Device>) => Promise<boolean>;
  /** Elimina dispositivo */
  deleteDevice: (id: number) => Promise<boolean>;
}

/**
 * Hook per la gestione completa dei dispositivi.
 *
 * Fornisce:
 * - Lista dispositivi con auto-fetch iniziale
 * - Operazioni CRUD (Create, Read, Update, Delete)
 * - Gestione stati loading/error
 *
 * @returns Oggetto con dati e metodi per gestire i dispositivi
 */
export function useDevices(): UseDevicesResult {
  const { apiBaseUrl } = useWhatsAppConfig();
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch lista apparati dal server
   * @param activeOnly - Se true, carica solo dispositivi attivi (default: true)
   */
  const fetchDevices = useCallback(async (activeOnly = true) => {
    setLoading(true);
    setError(null);

    try {
      const url = activeOnly
        ? `${apiBaseUrl}/devices?active_only=true`
        : `${apiBaseUrl}/devices`;
      const res = await fetch(url, { headers: waAuthHeaders() });
      const data: APIResponse<Device[]> = await res.json();

      if (data.success && data.data) {
        setDevices(data.data);
      } else {
        throw new Error(data.error || 'Errore caricamento apparati');
      }

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore sconosciuto');
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl]);

  /**
   * Crea nuovo apparato
   * @param device - Dati del nuovo dispositivo
   * @returns true se creato con successo
   */
  const createDevice = useCallback(async (device: Partial<Device>) => {
    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`${apiBaseUrl}/devices`, {
        method: 'POST',
        headers: waAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(device)
      });

      const data: APIResponse = await res.json();

      if (!data.success) {
        throw new Error(data.error || 'Creazione fallita');
      }

      await fetchDevices();
      return true;

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore creazione');
      return false;
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, fetchDevices]);

  /**
   * Aggiorna apparato esistente
   * @param id - ID del dispositivo
   * @param updates - Campi da aggiornare
   * @returns true se aggiornato con successo
   */
  const updateDevice = useCallback(async (id: number, updates: Partial<Device>) => {
    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`${apiBaseUrl}/devices/${id}`, {
        method: 'PUT',
        headers: waAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(updates)
      });

      const data: APIResponse = await res.json();

      if (!data.success) {
        throw new Error(data.error || 'Aggiornamento fallito');
      }

      await fetchDevices();
      return true;

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore aggiornamento');
      return false;
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, fetchDevices]);

  /**
   * Elimina apparato
   * @param id - ID del dispositivo da eliminare
   * @returns true se eliminato con successo
   */
  const deleteDevice = useCallback(async (id: number) => {
    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`${apiBaseUrl}/devices/${id}`, {
        method: 'DELETE',
        headers: waAuthHeaders(),
      });

      const data: APIResponse = await res.json();

      if (!data.success) {
        throw new Error(data.error || 'Eliminazione fallita');
      }

      await fetchDevices();
      return true;

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore eliminazione');
      return false;
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, fetchDevices]);

  // NOTA: Fetch iniziale rimosso per evitare race condition
  // Il consumer (es. WhatsAppDashboard) deve chiamare refresh() con il parametro desiderato
  // useEffect(() => { fetchDevices(); }, [fetchDevices]);

  return {
    devices,
    loading,
    error,
    refresh: fetchDevices,
    createDevice,
    updateDevice,
    deleteDevice
  };
}
